/**
 * ============================================================
 * MAGNESIUM OIL SPRAY CALCULATOR
 * Version: 2.0.0 | Updated: February 2026
 * Compatible with: gwb-calculators.js v5.2+
 *
 * Registry entry for gwb-calculators.js:
 * 'mg-oil': {
 *     name: 'Magnesium Oil Spray Calculator',
 *     initFnName: 'renderMgOilCalculator',
 *     file: 'mg-oil-calculator.js',
 *     available: true
 * }
 *
 * Tabs:
 *   1. Spray Dose    — how much elemental Mg am I getting?
 *   2. Bottle Duration — how long will my bottle last?
 *   3. DIY Converter  — how to make your own from MgCl2 powder
 * ============================================================
 */
(function () {
  'use strict';

  // ─── Constants ───────────────────────────────────────────────
  const MG_PER_SPRAY       = 10;     // mg elemental Mg per spray (1:1 ratio)
  const SPRAYS_PER_OZ      = 225;    // ~225 sprays per fluid oz
  const BOTTLE_SIZES       = [
    { oz: 8,  label: '8 oz (standard)',  sprays: 1800, mgCl2_g: 150, elMg_g: 17.7 },
    { oz: 16, label: '16 oz',            sprays: 3600, mgCl2_g: 300, elMg_g: 35.4 },
    { oz: 32, label: '32 oz (quart)',    sprays: 7200, mgCl2_g: 600, elMg_g: 70.8 }
  ];

  // DIY: elemental Mg % in USP MgCl2 hexahydrate = 11.95%
  const EL_MG_PERCENT = 0.1195;

  // Scoped DOM helper
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }

  // ─── Main render function ────────────────────────────────────
  window.renderMgOilCalculator = function (container) {

    container.innerHTML = `
      <div class="calculator-section">

        <!-- ── TABS ──────────────────────────────────────────── -->
        <div class="calc-tabs">
          <button class="calc-tab active" data-tab="dose-panel">💧 Spray Dose</button>
          <button class="calc-tab"        data-tab="duration-panel">📅 Bottle Duration</button>
          <button class="calc-tab"        data-tab="diy-panel">⚗️ DIY Converter</button>
        </div>

        <!-- ══════════════════════════════════════════════════════
             TAB 1 — SPRAY DOSE
             How much elemental magnesium am I getting?
        ══════════════════════════════════════════════════════ -->
        <div class="calc-section active" data-content="dose-panel">

          <p class="calc-hint">Based on our standard 1:1 ratio formula — approximately <strong>~10 mg elemental magnesium per spray</strong>.</p>

          <div class="calc-form">

            <div class="calc-row">
              <label>Sprays per application</label>
              <input type="number" class="dose-sprays" min="1" max="200" placeholder="e.g. 20" />
            </div>

            <div class="calc-row">
              <label>Applications per day</label>
              <input type="number" class="dose-freq" min="1" max="10" placeholder="e.g. 1" value="1" />
            </div>

            <div class="calc-row">
              <label>Quick start — sprays per application</label>
              <div class="calc-presets">
                <button class="preset-btn" data-target=".dose-sprays" data-value="10">10 sprays</button>
                <button class="preset-btn" data-target=".dose-sprays" data-value="15">15 sprays</button>
                <button class="preset-btn" data-target=".dose-sprays" data-value="20">20 sprays</button>
                <button class="preset-btn" data-target=".dose-sprays" data-value="40">40 sprays</button>
              </div>
            </div>

          </div>

          <div class="calc-buttons">
            <button class="calc-btn dose-calc-btn">Calculate</button>
            <button class="calc-btn-secondary dose-clear-btn">Clear</button>
          </div>

          <div class="calc-result dose-result"></div>

        </div>

        <!-- ══════════════════════════════════════════════════════
             TAB 2 — BOTTLE DURATION
             How long will my bottle last?
        ══════════════════════════════════════════════════════ -->
        <div class="calc-section" data-content="duration-panel">

          <p class="calc-hint">Estimate how long a bottle lasts based on your daily usage. Assumes ~225 sprays per fluid ounce.</p>

          <div class="calc-form">

            <div class="calc-row">
              <label>Bottle size</label>
              <select class="dur-size">
                ${BOTTLE_SIZES.map(b => `<option value="${b.oz}">${b.label} — ~${b.sprays.toLocaleString()} sprays</option>`).join('')}
              </select>
            </div>

            <div class="calc-row">
              <label>Sprays per day (total)</label>
              <input type="number" class="dur-sprays" min="1" max="500" placeholder="e.g. 20" />
            </div>

            <div class="calc-row">
              <label>Quick start — daily sprays</label>
              <div class="calc-presets">
                <button class="preset-btn" data-target=".dur-sprays" data-value="10">10/day</button>
                <button class="preset-btn" data-target=".dur-sprays" data-value="20">20/day</button>
                <button class="preset-btn" data-target=".dur-sprays" data-value="40">40/day</button>
                <button class="preset-btn" data-target=".dur-sprays" data-value="60">60/day</button>
              </div>
            </div>

          </div>

          <div class="calc-buttons">
            <button class="calc-btn dur-calc-btn">Calculate</button>
            <button class="calc-btn-secondary dur-clear-btn">Clear</button>
          </div>

          <div class="calc-result dur-result"></div>

        </div>

        <!-- ══════════════════════════════════════════════════════
             TAB 3 — DIY CONVERTER
             How to make your own from MgCl2 powder
        ══════════════════════════════════════════════════════ -->
        <div class="calc-section" data-content="diy-panel">

          <p class="calc-hint">Want to make your own magnesium oil from our <a href="/products/magnesium-chloride-usp" target="_blank" rel="noopener">Magnesium Chloride USP powder</a>? Enter your target batch size and ratio below.</p>

          <div class="calc-form">

            <div class="calc-row">
              <label>Batch size (fluid oz of finished spray)</label>
              <input type="number" class="diy-volume" min="1" max="128" placeholder="e.g. 8" />
            </div>

            <div class="calc-row">
              <label>Formula ratio</label>
              <select class="diy-ratio">
                <option value="0.5">1:1 ratio — Standard (our formula)</option>
                <option value="0.4">2:3 ratio — Lighter (60% MgCl₂)</option>
                <option value="0.6">3:2 ratio — Stronger (150% MgCl₂)</option>
              </select>
            </div>

            <div class="calc-row">
              <label>Quick batch sizes</label>
              <div class="calc-presets">
                <button class="preset-btn" data-target=".diy-volume" data-value="4">4 oz</button>
                <button class="preset-btn" data-target=".diy-volume" data-value="8">8 oz</button>
                <button class="preset-btn" data-target=".diy-volume" data-value="16">16 oz</button>
                <button class="preset-btn" data-target=".diy-volume" data-value="32">32 oz</button>
              </div>
            </div>

          </div>

          <div class="calc-buttons">
            <button class="calc-btn diy-calc-btn">Calculate</button>
            <button class="calc-btn-secondary diy-clear-btn">Clear</button>
          </div>

          <div class="calc-result diy-result"></div>

        </div>

      </div><!-- /.calculator-section -->
    `;

    // ─── Tab switching ──────────────────────────────────────────
    const tabs     = container.querySelectorAll('.calc-tab');
    const sections = container.querySelectorAll('.calc-section');

    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        tabs.forEach(t => t.classList.remove('active'));
        sections.forEach(s => s.classList.remove('active'));
        tab.classList.add('active');
        container.querySelector(`[data-content="${tab.dataset.tab}"]`).classList.add('active');
      });
    });

    // ─── Preset buttons ─────────────────────────────────────────
    // Each preset writes to the input identified by data-target selector,
    // marks itself active within its sibling group, then auto-calculates
    // if the sibling calc button is in a section that has enough inputs filled.
    container.querySelectorAll('.preset-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const targetSel = btn.dataset.target;
        const val       = btn.dataset.value;
        const input     = container.querySelector(targetSel);
        if (input) {
          input.value = val;
          // Mark active within this preset group
          btn.closest('.calc-presets').querySelectorAll('.preset-btn').forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          // Auto-calculate the current tab
          const section = btn.closest('.calc-section');
          if (section) {
            const calcBtn = section.querySelector('.calc-btn');
            if (calcBtn) calcBtn.click();
          }
        }
      });
    });

    // ─── Keyboard: Enter = calculate, Delete = clear ─────────────
    container.querySelectorAll('input[type="number"]').forEach(input => {
      input.addEventListener('keydown', e => {
        const section = input.closest('.calc-section');
        if (!section) return;
        if (e.key === 'Enter') {
          e.preventDefault();
          const btn = section.querySelector('.calc-btn');
          if (btn) btn.click();
        }
        if (e.key === 'Delete' && input.value === '') {
          const btn = section.querySelector('.calc-btn-secondary');
          if (btn) btn.click();
        }
      });
      // Auto-calculate on dropdown change when input has value
      input.addEventListener('change', () => {
        if (input.value) {
          const section = input.closest('.calc-section');
          const btn = section && section.querySelector('.calc-btn');
          if (btn) btn.click();
        }
      });
    });

    container.querySelectorAll('select').forEach(sel => {
      sel.addEventListener('change', () => {
        const section = sel.closest('.calc-section');
        const resultEl = section && section.querySelector('.calc-result');
        // Only auto-calc if a result is already showing
        if (resultEl && resultEl.innerHTML.trim() !== '') {
          const btn = section.querySelector('.calc-btn');
          if (btn) btn.click();
        }
      });
    });

    // ────────────────────────────────────────────────────────────
    // TAB 1 — SPRAY DOSE logic
    // ────────────────────────────────────────────────────────────
    const doseCalcBtn  = container.querySelector('.dose-calc-btn');
    const doseClearBtn = container.querySelector('.dose-clear-btn');
    const doseResult   = container.querySelector('.dose-result');

    function calcDose() {
      const sprays = parseFloat(container.querySelector('.dose-sprays').value);
      const freq   = parseFloat(container.querySelector('.dose-freq').value) || 1;

      if (!sprays || sprays <= 0) {
        doseResult.innerHTML = '<p style="color:#9c2b2e;">Please enter a valid number of sprays.</p>';
        scrollToResult(doseResult);
        return;
      }

      const mgPerApp  = sprays * MG_PER_SPRAY;
      const mgPerDay  = mgPerApp * freq;
      const rda       = 400; // typical adult RDA mg/day (for reference only)
      const rdaPct    = ((mgPerDay / rda) * 100).toFixed(0);

      doseResult.innerHTML = `
        <h4>Results</h4>
        <div style="margin-bottom:10px;">
          <strong>${sprays} sprays</strong> × ~${MG_PER_SPRAY} mg/spray
          = <strong>~${mgPerApp.toFixed(0)} mg elemental magnesium per application</strong>
        </div>
        ${freq > 1 ? `<div style="margin-bottom:10px;"><strong>${freq}× per day</strong> = ~${mgPerDay.toFixed(0)} mg/day elemental magnesium</div>` : ''}
        <div class="calc-tip">
          <strong>📋 Note:</strong> These are approximate values based on our 1:1 ratio formula.
          Actual absorption varies by individual. The commonly cited adult daily intake reference
          is ~310–420 mg — topical magnesium supplements this but does not replace a balanced diet.
          Individual results vary.
        </div>
      `;
      scrollToResult(doseResult);
    }

    doseCalcBtn.addEventListener('click', calcDose);
    doseClearBtn.addEventListener('click', () => {
      container.querySelector('.dose-sprays').value = '';
      container.querySelector('.dose-freq').value = '1';
      container.querySelectorAll('[data-content="dose-panel"] .preset-btn').forEach(b => b.classList.remove('active'));
      doseResult.innerHTML = '';
    });

    // ────────────────────────────────────────────────────────────
    // TAB 2 — BOTTLE DURATION logic
    // ────────────────────────────────────────────────────────────
    const durCalcBtn  = container.querySelector('.dur-calc-btn');
    const durClearBtn = container.querySelector('.dur-clear-btn');
    const durResult   = container.querySelector('.dur-result');

    function calcDuration() {
      const oz         = parseFloat(container.querySelector('.dur-size').value);
      const perDay     = parseFloat(container.querySelector('.dur-sprays').value);
      const bottle     = BOTTLE_SIZES.find(b => b.oz === oz) || BOTTLE_SIZES[0];

      if (!perDay || perDay <= 0) {
        durResult.innerHTML = '<p style="color:#9c2b2e;">Please enter your sprays per day.</p>';
        scrollToResult(durResult);
        return;
      }

      const totalSprays = bottle.sprays;
      const days        = totalSprays / perDay;
      const weeks       = days / 7;
      const months      = days / 30.44;
      const mgPerDay    = perDay * MG_PER_SPRAY;

      let durationStr;
      if (days < 14)        durationStr = `<strong>${Math.round(days)} days</strong>`;
      else if (days < 60)   durationStr = `<strong>${weeks.toFixed(1)} weeks</strong> (${Math.round(days)} days)`;
      else                  durationStr = `<strong>${months.toFixed(1)} months</strong> (${Math.round(days)} days)`;

      // Recommend bottle size
      let recoMsg = '';
      if (days < 30) {
        recoMsg = `<div class="calc-tip"><strong>💡 Tip:</strong> At this usage rate you go through a bottle quickly — consider our <a href="/products/magnesium-oil-spray" target="_blank" rel="noopener">multi-pack</a> or making your own using <a href="/products/magnesium-chloride-usp" target="_blank" rel="noopener">Magnesium Chloride USP powder</a> for better value per ounce.</div>`;
      }

      durResult.innerHTML = `
        <h4>Results — ${bottle.label}</h4>
        <div style="margin-bottom:10px;">
          At <strong>${perDay} sprays/day</strong>, your ${oz} oz bottle will last approximately ${durationStr}.
        </div>
        <div style="margin-bottom:6px;"><strong>Daily elemental magnesium:</strong> ~${mgPerDay.toFixed(0)} mg/day</div>
        <div style="margin-bottom:6px;"><strong>Total sprays in bottle:</strong> ~${totalSprays.toLocaleString()}</div>
        <div style="margin-bottom:12px;"><strong>Total elemental magnesium in bottle:</strong> ~${(bottle.elMg_g * 1000).toLocaleString()} mg</div>
        ${recoMsg}
        <div class="calc-tip">
          <strong>📋 Note:</strong> Based on ~225 sprays per fluid ounce. Actual spray count varies slightly by pump and technique.
        </div>
      `;
      scrollToResult(durResult);
    }

    durCalcBtn.addEventListener('click', calcDuration);
    durClearBtn.addEventListener('click', () => {
      container.querySelector('.dur-sprays').value = '';
      container.querySelector('.dur-size').selectedIndex = 0;
      container.querySelectorAll('[data-content="duration-panel"] .preset-btn').forEach(b => b.classList.remove('active'));
      durResult.innerHTML = '';
    });

    // ────────────────────────────────────────────────────────────
    // TAB 3 — DIY CONVERTER logic
    // ────────────────────────────────────────────────────────────
    const diyCalcBtn  = container.querySelector('.diy-calc-btn');
    const diyClearBtn = container.querySelector('.diy-clear-btn');
    const diyResult   = container.querySelector('.diy-result');

    function calcDiy() {
      const volOz    = parseFloat(container.querySelector('.diy-volume').value);
      const ratio    = parseFloat(container.querySelector('.diy-ratio').value); // fraction of MgCl2 in total

      if (!volOz || volOz <= 0) {
        diyResult.innerHTML = '<p style="color:#9c2b2e;">Please enter your desired batch size in fluid ounces.</p>';
        scrollToResult(diyResult);
        return;
      }

      // 1 fl oz water ≈ 29.574 g
      const totalG      = volOz * 29.574;
      // At 1:1 ratio: half the weight is MgCl2, half is water
      // ratio value = fraction of MgCl2 (0.5 for 1:1)
      const mgCl2_g     = totalG * ratio;
      const water_g     = totalG - mgCl2_g;
      const water_oz    = (water_g / 29.574).toFixed(1);
      const mgCl2_tbsp  = (mgCl2_g / 14.4).toFixed(1); // ~14.4g per tbsp for MgCl2 flakes
      const mgCl2_cup   = (mgCl2_g / 230).toFixed(2);  // ~230g per cup
      const elMg_total  = (mgCl2_g * EL_MG_PERCENT * 1000).toFixed(0);
      const estSprays   = Math.round(volOz * SPRAYS_PER_OZ);
      const mgPerSpray  = (parseFloat(elMg_total) / estSprays).toFixed(1);

      diyResult.innerHTML = `
        <h4>DIY Recipe — ${volOz} oz batch</h4>
        <div style="margin-bottom:8px;">
          <strong>Magnesium Chloride USP powder:</strong> ${mgCl2_g.toFixed(1)} g
          &nbsp;(≈ ${mgCl2_tbsp} tbsp)
        </div>
        <div style="margin-bottom:8px;">
          <strong>Distilled or RO water:</strong> ${water_g.toFixed(1)} g &nbsp;(≈ ${water_oz} fl oz)
        </div>
        <div style="margin-bottom:8px;"><strong>Estimated sprays:</strong> ~${estSprays.toLocaleString()}</div>
        <div style="margin-bottom:12px;"><strong>Estimated elemental Mg per spray:</strong> ~${mgPerSpray} mg</div>
        <div class="calc-tip">
          <strong>📋 How to mix:</strong>
          <ol style="margin:8px 0 0 18px; line-height:1.7;">
            <li>Measure ${mgCl2_g.toFixed(0)} g of <a href="/products/magnesium-chloride-usp" target="_blank" rel="noopener">Magnesium Chloride USP</a> powder</li>
            <li>Heat ${water_g.toFixed(0)} g of distilled or RO water until warm (not boiling)</li>
            <li>Stir the powder into the warm water until fully dissolved</li>
            <li>Allow to cool completely before pouring into your glass spray bottle</li>
            <li>Label with the date and ratio used</li>
          </ol>
        </div>
        <div style="font-size:0.85em; color:#666; margin-top:10px;">
          Weights are approximate. Use a kitchen scale for accuracy. Always use distilled or RO water — tap water minerals can affect the solution.
        </div>
      `;
      scrollToResult(diyResult);
    }

    diyCalcBtn.addEventListener('click', calcDiy);
    diyClearBtn.addEventListener('click', () => {
      container.querySelector('.diy-volume').value = '';
      container.querySelector('.diy-ratio').selectedIndex = 0;
      container.querySelectorAll('[data-content="diy-panel"] .preset-btn').forEach(b => b.classList.remove('active'));
      diyResult.innerHTML = '';
    });

    // ─── Mobile scroll helper ────────────────────────────────────
    function scrollToResult(el) {
      if (window.innerWidth <= 768) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
      }
    }

  }; // end renderMgOilCalculator

})();